import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Users, Car, Phone } from "lucide-react";
import { SITE } from "../data/site";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal";

export default function BookingWidget({ compact = false, title }) {
  const { t } = useLang();
  const [f, setF] = useState({ pickup: "", drop: "", date: "", time: "", passengers: "1", vehicle: "Sedan", website: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.pickup.trim() || !f.drop.trim() || !f.date) {
      toast.error("Please fill pickup, drop and travel date.");
      return;
    }
    setSending(true);
    const msg = `Hello Vikash ji, I want to book a taxi.%0A%0APickup: ${f.pickup}%0ADrop: ${f.drop}%0ADate: ${f.date}%0ATime: ${f.time || "Flexible"}%0APassengers: ${f.passengers}%0AVehicle: ${f.vehicle}%0A%0APlease share availability and fare.`;
    try {
      await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, source: "booking_widget" }),
      });
    } catch {}
    window.open(`https://wa.me/${SITE.whatsapp}?text=${msg}`, "_blank", "noopener");
    toast.success("Opening WhatsApp with your trip details…");
    setSending(false);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="booking-widget"
      className="bg-white rounded-2xl shadow-2xl shadow-navy/20 border border-slate-100 p-5 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="font-heading font-bold text-lg text-navy">{title || t("wTitle")}</h2>
        <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-gold-dark bg-gold-light px-2.5 py-1 rounded-full">
          {t("wFree")}
        </span>
      </div>
      <div className={`grid gap-3 ${compact ? "" : "sm:grid-cols-2"}`}>
        <label className="relative block">
          <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-royal" />
          <input className={`${inputCls} pl-9`} placeholder={t("wPickup")} value={f.pickup} onChange={set("pickup")} data-testid="booking-pickup-input" required />
        </label>
        <label className="relative block">
          <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold-dark" />
          <input className={`${inputCls} pl-9`} placeholder={t("wDrop")} value={f.drop} onChange={set("drop")} data-testid="booking-drop-input" required />
        </label>
        <label className="relative block">
          <CalendarDays size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-royal" />
          <input type="date" min={new Date().toISOString().slice(0, 10)} className={`${inputCls} pl-9`} value={f.date} onChange={set("date")} data-testid="booking-date-input" required />
        </label>
        <label className="relative block">
          <Clock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-royal" />
          <input type="time" className={`${inputCls} pl-9`} value={f.time} onChange={set("time")} data-testid="booking-time-input" />
        </label>
        <label className="relative block">
          <Users size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-royal" />
          <select className={`${inputCls} pl-9`} value={f.passengers} onChange={set("passengers")} data-testid="booking-passengers-select">
            {[1, 2, 3, 4, 5, 6, "7-12", "13-17", "18+"].map((p) => (
              <option key={p} value={p}>{p} {p === 1 ? t("wPassenger") : t("wPassengers")}</option>
            ))}
          </select>
        </label>
        <label className="relative block">
          <Car size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-royal" />
          <select className={`${inputCls} pl-9`} value={f.vehicle} onChange={set("vehicle")} data-testid="booking-vehicle-select">
            {["Sedan", "SUV", "Premium SUV", "Luxury Sedan", "Tempo Traveller", "Large Group Vehicle"].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="booking-submit-btn"
        className="mt-4 w-full rounded-xl bg-gold text-navy font-heading font-extrabold text-base py-3.5 tracking-wide hover:bg-gold-dark hover:text-white transition-colors disabled:opacity-60"
      >
        {sending ? t("wSending") : t("wSubmit")}
      </button>
      <a
        href={`tel:${SITE.phoneTel}`}
        data-testid="booking-call-link"
        className="mt-2.5 w-full rounded-xl border-2 border-royal text-royal font-bold text-sm py-3 flex items-center justify-center gap-2 hover:bg-royal hover:text-white transition-colors"
      >
        <Phone size={15} /> {t("wOrCall")} {SITE.phoneDisplay}
      </a>
    </form>
  );
}
